import { FolderPermissionActionI, FolderPermissionActionType } from '../models/IrisDMSFolderPermission';
import { computed, inject, Injectable, Injector, Signal } from '@angular/core';
import { IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';
import { IrisDmsFoldersSandbox } from '@iris/common/modules/dms/sandbox/dms-folders.sandbox';
import { IrisModules } from '@iris/common/models/IrisModulesEnums';
import { IrisSecurityService } from '@iris/common/modules/security/services/security.service';
import { IrisDMSFolderI } from '@iris/common/modules/dms/models/IrisDMSFolder';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { uniq } from 'lodash';
import { IrisDmsFolderPermissionsStore } from '../store/folder-permissions.signal-store';
import { IrisDmsUserPermissionI, IrisDmsUserPermissionsStore } from '../store/user-permissions.signal-store';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, Observable, shareReplay, take, tap } from 'rxjs';
import { IrisDmsSettingsSandbox } from './dms-settings.sandbox';
import { IrisDmsContentStore } from '../store/dms-content.signal-store';

@Injectable({ providedIn: 'root' })
export class IrisDmsPermissionsSandbox {
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly securityService = inject(IrisSecurityService);
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly dmsSettingsSandbox = inject(IrisDmsSettingsSandbox);
  private readonly injector = inject(Injector);

  private readonly dmsFolderPermissionsStore = inject(IrisDmsFolderPermissionsStore);
  private readonly dmsUserPermissionsStore = inject(IrisDmsUserPermissionsStore);
  private readonly dmsContentStore = inject(IrisDmsContentStore);

  private readonly foldersActionsMap = new Map<string, Observable<IrisDmsUserPermissionI[]>>();

  // admin or dms config methods

  get isAdmin(): boolean {
    return this.securityService.isAdmin;
  }

  get isDmsConfigUser(): boolean {
    return this.dmsSettingsSandbox.userGroupsEnabled() && this.securityService.hasConfigRights(IrisModules.DMS);
  }

  get isAdminOrDmsConfigUser(): boolean {
    return this.isAdmin || this.isDmsConfigUser;
  }

  get isAccessOnly(): boolean {
    return this.dmsSettingsSandbox.userGroupsEnabled() && this.securityService.hasOnlyAccessRightsForDms;
  }

  get dmsSecurityActionsList(): FolderPermissionActionI[] {
    return [
      { action: FolderPermissionActionType.Read, icon: 'fa-eye', label: 'label.dms.Read' },
      { action: FolderPermissionActionType.Update, icon: 'fa-pencil-alt', label: 'label.dms.Edit' },
    ];
  }

  readonly hasUpdatePermissionForSelectedFiles = computed(() => {
    const selectedFiles = this.dmsContentStore.selectedFiles();
    const foldersIds = uniq(selectedFiles.map(file => file.parentId));
    return foldersIds.every(folderId => this.hasUpdatePermissionForFolderId(folderId)());
  });

  private readonly selectedFolderId = this.dmsFoldersSandbox.selectedFolderId;
  readonly hasUpdatePermissionForSelectedFolder = computed(() => {
    return this.hasUpdatePermissionForFolderId(this.selectedFolderId())();
  });

  private readonly projectRootFolderId = toSignal(this.dmsFoldersSandbox.currentProjectRootFolderId$);
  readonly hasUpdatePermissionForProjectFolder = computed(() => {
    return this.hasUpdatePermissionForFolderId(this.projectRootFolderId())();
  });

  private readonly isSelectedFolderNotLocked = this.dmsFoldersSandbox.isSelectedFolderNotLocked;
  private readonly currentProjectId = toSignal(this.globalSandbox.currentProjectId$);

  readonly hasUserUpdatePermissionAndFolderNotLocked = computed(() => {
    return this.hasUpdatePermissionForSelectedFolder() && this.isSelectedFolderNotLocked()
      || !!this.currentProjectId() && this.hasUpdatePermissionForProjectFolder();
  });

  readonly canUpdateCurrentProject = computed(() => {
    return this.securityService.hasPermissions(this.currentProjectId(), 'Project', 'update');
  });

  hasReadPermissionForFolderId = (folderId: string): Signal<boolean> => this.dmsUserPermissionsStore.hasReadPermission(folderId);
  hasReadPermissionForFile = (file: IrisDMSFileI): Signal<boolean> => this.hasReadPermissionForFolderId(file?.parentId);
  hasReadPermissionForFolder = (folder: IrisDMSFolderI): Signal<boolean> => this.hasReadPermissionForFolderId(folder?.id);
  hasUpdatePermissionForFolderId = (folderId: string): Signal<boolean> => this.dmsUserPermissionsStore.hasUpdatePermission(folderId);
  hasUpdatePermissionForFile = (file: IrisDMSFileI): Signal<boolean> => this.hasUpdatePermissionForFolderId(file?.parentId);
  hasUpdatePermissionForFolder = (folder: IrisDMSFolderI): Signal<boolean> => this.hasUpdatePermissionForFolderId(folder?.id);

  hasUserPermissionForFolderRequest = (folderId: string, actionType: FolderPermissionActionType): Signal<boolean> => computed(() => {
    const actions = this.getUserAvailableActions(folderId);
    return actions.some(({ action, allowed } ) => action === actionType && allowed);
  });
  hasUserReadPermissionForFolderRequest = (folderId: string): Signal<boolean> =>
    this.hasUserPermissionForFolderRequest(folderId, FolderPermissionActionType.Read);
  
  hasUserUpdatePermissionForFolderRequest = (folderId: string): Signal<boolean> =>
    this.hasUserPermissionForFolderRequest(folderId, FolderPermissionActionType.Update);

  hasUserUpdatePermissionForFolderRequest$ = (folderId: string): Observable<boolean> =>
    this.getUserAvailableActions$(folderId).pipe(
      take(1),
      map(actions => actions.some(({ action, allowed } ) => action === FolderPermissionActionType.Update && allowed)),
    );

  readonly hasUserUpdatePermissionsForSelectedFolders = computed(() => {
    const foldersIds = this.dmsContentStore.selectedFoldersIds();
    if (!foldersIds.length) return true;
    return foldersIds.every(folderId => this.hasUpdatePermissionForFolderId(folderId)());
  });

  hasFolderPermissionByUserGroupAndAction(userGroupId: number, action: FolderPermissionActionType): Signal<boolean> {
    return this.dmsFolderPermissionsStore.hasFolderPermissionByUserGroupAndAction(userGroupId, action);
  }

  hasUserUpdatePermission(folderId: string, action: FolderPermissionActionType): boolean {
    return this.securityService.hasPermissions(folderId, 'iris:repoContentFolder', action);
  }

  isFolderLoading = (folderId: string): Signal<boolean> => computed(() => this.dmsUserPermissionsStore.isFolderLoading(folderId));

  // main methods

  getUserAvailableActions = (folderId: string): IrisDmsUserPermissionI[] => {
    const isFolderLoading = this.dmsUserPermissionsStore.isFolderLoading(folderId);
    const actions = this.dmsUserPermissionsStore.getUserAvailableActions(folderId)();

    if (!actions?.length && !isFolderLoading) {
      this.getUserAvailableActionsForFolder(folderId);
    }
    return actions;
  };

  getUserAvailableActions$(folderId: string): Observable<IrisDmsUserPermissionI[]> {
    if (!this.foldersActionsMap.has(folderId)) {
      const availableActions$ = toObservable(this.dmsUserPermissionsStore.getUserAvailableActions(folderId), { injector: this.injector });
      const actions$ = availableActions$.pipe(
        filter(() => !this.isFolderLoading(folderId)()),
        tap(actions => {
          const isFolderLoading = this.isFolderLoading(folderId)();
          if (!actions?.length && !isFolderLoading) {
            this.getUserAvailableActionsForFolder(folderId);
          }
        }),
        filter(actions => !!actions?.length),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        }),
      );
      this.foldersActionsMap.set(folderId, actions$);
    }
    return this.foldersActionsMap.get(folderId);
  }

  getUserAvailableActionsForFile(file: IrisDMSFileI): void {
    this.getUserAvailableActionsForFolder(file?.parentId);
  }

  getUserAvailableActionsForFolder(folderId: string): void {
    this.dmsUserPermissionsStore.fetchUserPermissions$({ folderId, skipRequest: !this.dmsSettingsSandbox.userGroupsEnabled() });
  }

  getFolderPermissions(folderId: string): void {
    this.dmsFolderPermissionsStore.fetchFolderPermissions$(folderId);
  }

  toggleFolderPermissionForUserGroup(
    folderId: string,
    userGroupId: number,
    action: FolderPermissionActionType,
    allowed: boolean,
  ): void {
    this.dmsFolderPermissionsStore.toggleFolderPermission({ folderId, userGroupId, action, allowed });
  }

  resetUserPermissionsForFolder(folderId: string): void {
    this.foldersActionsMap.delete(folderId);
    this.dmsUserPermissionsStore.resetPermissions(folderId);
  }
}
